import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";
import MainLayout from "./layouts/MainLayout";

import Login from "./pages/Login";

import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetails from "./components/tickets/TicketDetails";
import Board from "./pages/Board";
import TaskDetails from "./components/board/TaskDetails";
import Employees from "./pages/Employees";
import Departments from "./pages/Departments";
import Reports from "./pages/Reports";
import Projects from "./pages/Projects";
import ProjectPlanning from "./pages/ProjectPlanning";
import ResourceManagement from "./pages/ResourceManagement";
import RoleManagement from "./pages/RoleManagement";
import Settings from "./pages/Settings";
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./config/msalConfig";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { UserRoleProvider } from "./context/UserRoleContext";
import { RolesProvider } from "./context/RolesContext";
import { DepartmentsProvider } from "./context/DepartmentsContext";
import { EmployeesProvider } from "./context/EmployeesContext";
import AutoLogout from "./config/AutoLogout";

function App() {
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("session_expired") === "true") {
      sessionStorage.removeItem("session_expired");
      setSessionExpiredOpen(true);
    }
  }, []);

  return (
    <MsalProvider instance={msalInstance}>
      <UserRoleProvider>
        <AutoLogout />
        <RolesProvider>
          <DepartmentsProvider>
            <EmployeesProvider>
            <Routes>
              {/* LOGIN PAGE */}

              <Route path="/login" element={<Login />} />

          {/* APPLICATION */}

          <Route element={<MainLayout />}>
            <Route path="/" element={<Navigate to="/login" replace />} />

            <Route
              path="/dashboard"
              element={(
                <ProtectedRoute pageKey="dashboard">
                  <Dashboard />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/tickets"
              element={(
                <ProtectedRoute pageKey="tickets">
                  <Tickets />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/tickets/:ticketId"
              element={(
                <ProtectedRoute pageKey="tickets">
                  <TicketDetails />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/board"
              element={(
                <ProtectedRoute pageKey="board">
                  <Board />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/board/:projectId"
              element={(
                <ProtectedRoute pageKey="board">
                  <Board />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/board/:projectId/task/:taskId"
              element={(
                <ProtectedRoute pageKey="board">
                  <TaskDetails />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/employees"
              element={(
                <ProtectedRoute pageKey="employees">
                  <Employees />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/departments"
              element={(
                <ProtectedRoute pageKey="departments">
                  <Departments />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/projects"
              element={(
                <ProtectedRoute pageKey="projects">
                  <Projects />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/project-planning"
              element={(
                <ProtectedRoute pageKey="project_planning">
                  <ProjectPlanning />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/resource-management"
              element={(
                <ProtectedRoute pageKey="resource_management">
                  <ResourceManagement />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/reports"
              element={(
                <ProtectedRoute pageKey="reports">
                  <Reports />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/roles"
              element={(
                <ProtectedRoute pageKey="role_management">
                  <RoleManagement />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/settings"
              element={(
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              )}
            />
          </Route>
        </Routes>
            </EmployeesProvider>
      </DepartmentsProvider>
      </RolesProvider>
      </UserRoleProvider>
      <Snackbar
        open={sessionExpiredOpen}
        autoHideDuration={6000}
        onClose={() => setSessionExpiredOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="warning" onClose={() => setSessionExpiredOpen(false)}>
          Your session has expired. You have been logged in again.
        </Alert>
      </Snackbar>
      <AutoLogout />
    </MsalProvider>
  );
}

export default App;
